import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import _ from "lodash";

import { Button } from "src/app/components/Button/Button";
import { Dropdown, DropdownMenu } from "src/app/components/Dropdown/Dropdown";
import { ReactComponent as FilterIcon } from "src/images/filter.svg";
import { ReactComponent as RubberIcon } from "src/images/rubber.svg";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import CustomCheckbox from "src/app/components/CustomCheckbox/CustomCheckbox";
import { CustomDateRange } from "src/app/components/CustomDateRange/CustomDateRange";
import { GalleryFilters } from "src/types";
import { gallerySocialProviders } from "./utils";

import "./GalleryFiltersDropdown.scss";

interface Props {
  filters: GalleryFilters;
  setFilters: Dispatch<SetStateAction<GalleryFilters>>;
  initialFilters: GalleryFilters;
}

export const getFiltersCounter = (filtersObject: GalleryFilters) => {
  const platformsChangedCount =
    filtersObject.platforms.length !== gallerySocialProviders.length ? 1 : 0;

  const datesChangedCount =
    filtersObject.dateFrom && filtersObject.dateTo ? -1 : 0;

  return (
    Object.values(filtersObject).filter((item) => item).length -
    1 +
    platformsChangedCount +
    datesChangedCount
  );
};

const MIN_DATE_OFFSET_DAYS = 730;

export default function GalleryFiltersDropdown(props: Props) {
  const { filters, setFilters, initialFilters } = props;

  const [localFilters, setLocalFilters] = useState<GalleryFilters>(filters);
  const [dateFrom, setDateFrom] = useState<string | null>(filters.dateFrom);
  const [dateTo, setDateTo] = useState<string | null>(filters.dateTo);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const filtersCounter = getFiltersCounter(filters);
  const localFiltersCounter = getFiltersCounter(localFilters);

  useEffect(() => {
    setLocalFilters(filters);
    setDateFrom(filters.dateFrom);
    setDateTo(filters.dateTo);
  }, [filters]);

  useEffect(() => {
    if (filters.dateFrom === dateFrom && filters.dateTo === dateTo) return;

    setLocalFilters((prevLocalFilters) => ({
      ...prevLocalFilters,
      dateFrom,
      dateTo,
    }));
  }, [dateFrom, dateTo]);

  const togglePlatform = (platform: string) => {
    if (localFilters.platforms.includes(platform)) {
      setLocalFilters({
        ...localFilters,
        platforms: localFilters.platforms.filter((p) => p !== platform),
      });
    } else {
      setLocalFilters({
        ...localFilters,
        platforms: [...localFilters.platforms, platform],
      });
    }
  };

  const toggleAllPlatforms = () => {
    if (localFilters.platforms.length === gallerySocialProviders.length) {
      setLocalFilters({
        ...localFilters,
        platforms: [],
      });
    } else {
      setLocalFilters({
        ...localFilters,
        platforms: gallerySocialProviders.map((p) => p.value),
      });
    }
  };

  const filtersSelected = filtersCounter > 0;

  const resetLocalFilters = () => {
    setLocalFilters(filters);
    setDateFrom(filters.dateFrom);
    setDateTo(filters.dateTo);
  };

  const applyFilters = () => {
    setFilters(localFilters);
  };

  return (
    <Dropdown
      className="gallery-filters-dropdown"
      disableOutsideClick={isDatePickerOpen}
      outsideClickCallback={resetLocalFilters}
    >
      {(isOpen, setIsOpen) => (
        <>
          <div className="gallery-filters-dropdown__buttons">
            <Button
              variant={filtersSelected ? "light-grey" : "white"}
              size="medium"
              onClick={() => {
                resetLocalFilters();
                setIsOpen(!isOpen);
              }}
              dataQa="filter-button"
            >
              <FilterIcon />
              <IDHFormattedMessage id="ws_filter" defaultMessage="Filter" />
              {filtersSelected && `: ${filtersCounter}`}
            </Button>

            {filtersSelected && (
              <Button
                variant="white"
                size="medium"
                onClick={() => setFilters(initialFilters)}
              >
                <RubberIcon />
                <IDHFormattedMessage
                  id="ws_clear_filters"
                  defaultMessage="Clear filters"
                />
              </Button>
            )}
          </div>

          <DropdownMenu
            className="gallery-filters-dropdown__menu"
            isOpen={isOpen}
          >
            {/* <div className="gallery-filters-dropdown__switch-wrapper">
              <CustomSwitch
                checked={localFilters.isMyCampaignsOnly}
                onChange={() =>
                  setLocalFilters({
                    ...localFilters,
                    isMyCampaignsOnly: !localFilters.isMyCampaignsOnly,
                  })
                }
                dataAttributes={{ dataQa: "my-campaigns-only-switch" }}
              />
              <IDHFormattedMessage
                id="ws_show_publications_only_from_my_campaigns"
                defaultMessage="Show publications only from my campaigns"
              />
            </div> */}
            <h5 className="gallery-filters-dropdown__section-header">
              <IDHFormattedMessage
                id="ws_social_platform"
                defaultMessage="Social platform"
              />
            </h5>
            <div className="gallery-filters-dropdown__platforms">
              <div className="gallery-filters-dropdown__checkbox-wrapper">
                <CustomCheckbox
                  id="all"
                  name="all"
                  checked={
                    localFilters.platforms.length ===
                    gallerySocialProviders.length
                  }
                  onChange={toggleAllPlatforms}
                  data-qa="filters-all-checkbox"
                />
                <span>
                  <IDHFormattedMessage id="ws_all" defaultMessage="All" />
                </span>
              </div>

              {gallerySocialProviders.map((provider) => (
                <div className="gallery-filters-dropdown__checkbox-wrapper">
                  <CustomCheckbox
                    id={provider.value}
                    name={provider.value}
                    checked={localFilters.platforms.includes(provider.value)}
                    onChange={() => togglePlatform(provider.value)}
                    data-qa={`filters-${provider.value}-checkbox`}
                  />
                  <span>{provider.label}</span>
                </div>
              ))}
            </div>
            <h5 className="gallery-filters-dropdown__section-header">
              <IDHFormattedMessage
                id="ws_publication_time"
                defaultMessage="Publication time"
              />
            </h5>
            <CustomDateRange
              dateFrom={dateFrom}
              setDateFrom={setDateFrom}
              dateTo={dateTo}
              setDateTo={setDateTo}
              toRight
              setIsPickerOpen={setIsDatePickerOpen}
              minDate={
                new Date(
                  Date.now() - MIN_DATE_OFFSET_DAYS * 24 * 60 * 60 * 1000,
                )
              }
              maxDate={new Date()}
            />

            <div className="gallery-filters-dropdown__action-buttons">
              <Button
                variant="white-with-light-grey-border"
                size="small"
                onClick={() => {
                  resetLocalFilters();
                  setIsOpen(false);
                }}
                dataQa="close-filters-button"
              >
                <IDHFormattedMessage id="ws_close" defaultMessage="Close" />
              </Button>

              {localFiltersCounter > 0 && (
                <Button
                  variant="white-with-light-grey-border"
                  size="small"
                  onClick={() => setFilters(initialFilters)}
                  dataQa="clear-all-filters-button"
                >
                  <IDHFormattedMessage
                    id="ws_clear_all"
                    defaultMessage="Clear all"
                  />
                </Button>
              )}

              {!_.isEqual(localFilters, filters) && (
                <Button
                  variant="blue"
                  size="small"
                  onClick={applyFilters}
                  dataQa="apply-filters-button"
                >
                  <IDHFormattedMessage
                    id="ws_apply_filters"
                    defaultMessage="Apply filters"
                  />
                </Button>
              )}
            </div>
          </DropdownMenu>
        </>
      )}
    </Dropdown>
  );
}
